<script lang="ts">
import { computed, defineComponent, inject, onActivated, onMounted, onUnmounted, PropType, ref, watch } from "vue";
import Steps from "@/components/Steps.vue";
import formatUtils from "@/businesslogic/utils/formatUtils";
import BeitragFH from "@/components/BeitragFH.vue";
import EmailOderHerunterladen from "@/components/vorschlag/EmailOderHerunterladen.vue";
import { NavvItem } from "@/model/NavItem";
import Produktmodul from "@/components/module/Produktmodul.vue";
import ProduktmoduleFH from "@/components/module/produktmoduleFH2024";
import LeistungstabelleEineLinie from "@/components/leistungstabelle/LeistungstabelleEineLinie.vue";
import TarifauswahlEineLinie from "@/components/tarif/TarifauswahlEineLinie.vue";
import formValidierung from "@/businesslogic/formValidierung";
import IconSprite from "@/components/IconSprite.vue";
import leistungenFH from "@/components/leistungstabelle/LeistungenFH2024";
import TarifoptionEineLinie from "@/components/tarifoptionen/TarifoptionEineLinie.vue";
import TarifoptionInformationenFH from "@/components/tarifoptionen/TarifoptionInformationenFH";
import Logger from "js-logger";
import ValidierungsFehler from "@/components/ValidierungsFehler.vue";
import storeUtils from "@/businesslogic/utils/storeUtils";
import AusschlussmodulElementar from "@/components/module/ausschlussmodule/ausschlussmodulElementar";
import ProduktmodulAusschluss from "@/components/module/ProduktmodulAusschluss.vue";
import dataUtils from "@/businesslogic/utils/dataUtils";
import HugRabatt from "@/components/HugRabatt.vue";
import { useFormStore } from "@/businesslogic/stores/formStore";

export default defineComponent({
  name: "TarifFH2024",
  emits: ["prev-page", "next-page", "goto-page", "handle-error"],
  components: {
    HugRabatt,
    ProduktmodulAusschluss,
    TarifoptionEineLinie,
    LeistungstabelleEineLinie,
    Steps,
    BeitragFH,
    EmailOderHerunterladen,
    Produktmodul,
    TarifauswahlEineLinie,
    IconSprite,
    ValidierungsFehler,
  },
  props: {
    navItems: Object as PropType<NavvItem[]>,
  },
  setup(props, { emit }) {
    const piniaStore = useFormStore();
    const istSichtbar: any = inject("istSichtbar");
    const wechsleSichtbarkeit: any = inject("wechsleSichtbarkeit");
    const { formatCurrency } = formatUtils();
    const { validiereForm, getFormFehlerAnzahl, getFormFehler } = formValidierung();
    const fehler = ref({} as any);
    const produktbezeichnung = ref("Ferienhausversicherung");
    const tarifoptionen = ref(TarifoptionInformationenFH);
    const produktmodule = ref(ProduktmoduleFH);
    const tibUrlEinProdukt = ref("");
    const tibUrlMultiProduktVgv = ref("");
    const tibUrlMultiProduktVhv = ref("");
    const hatVorschaeden = ref(false);
    const ausschlussModul = ref(AusschlussmodulElementar);
    const {
      istMehrfamilienhaus,
      istEinfamilienhaus,
      istVGVundVHV,
      istNurVHV,
      istNurVGV,
      enthaeltVHV,
      enthaeltVGV,
      istPhotovoltaikVersicherungMoeglich,
    } = storeUtils();
    const { lte, gte } = dataUtils();
    const leistungen = ref(leistungenFH(enthaeltVGV(piniaStore.state), enthaeltVHV(piniaStore.state)));
    const ueberschwemmungText = computed(() => {
      return piniaStore.state.zuersZone.zone === "4"
        ? "Rückstau und Überschwemmung sind nicht versichert"
        : "Überschwemmung aufgrund Starkregen";
    });

    const fensterBreite = ref(window.innerWidth);
    const behandleGroessenAenderung = () => {
      fensterBreite.value = window.innerWidth;
      wechsleTarif();
    };

    function populiereSb(optionen: number[]): void {
      let bisherigeSbWeiterhinVorhanden = false;
      const selectoptionen = [];
      for (const option of optionen) {
        selectoptionen.push({
          label: option > 0 ? option + " €" : "Keine",
          value: option,
        });
        if (option == piniaStore.state.tarifinformationen.selbstbeteiligung) {
          bisherigeSbWeiterhinVorhanden = true;
        }
      }
      if (!bisherigeSbWeiterhinVorhanden) {
        console.debug(
          "Bisherige SB nicht mehr wählbar > Zurücksetzen.",
          piniaStore.state.ferienhaus.versicherungsumfang,
        );
        piniaStore.state.tarifinformationen.selbstbeteiligung = 250;
      }
      const tarifoption = tarifoptionen.value.find((i) => i.typ === "selbstbeteiligung");
      if (tarifoption) {
        tarifoption.selectoptionen = selectoptionen;
      }
    }

    function wechsleTarif(): void {
      const urlPrefix = "https://www.gev-versicherung.de/downloads/pdf/";
      tibUrlEinProdukt.value = "";
      tibUrlMultiProduktVgv.value = urlPrefix + "TI-TIB-VG-FHK-2023.pdf";
      tibUrlMultiProduktVhv.value = urlPrefix + "TI-TIB-HR-FHK-2024.pdf";
      switch (piniaStore.state.ferienhaus.versicherungsumfang) {
        case "0001":
          if (fensterBreite.value < 768) {
            produktbezeichnung.value = "Wohngebäude & Hausrat";
          } else {
            produktbezeichnung.value = "Wohngebäudeversicherung & Hausratversicherung";
          }
          populiereSb([0, 250, 500]);
          break;
        case "0002":
          produktbezeichnung.value = "Wohngebäudeversicherung";
          tibUrlEinProdukt.value = tibUrlMultiProduktVgv.value;
          populiereSb([0, 250, 500, 1000]);
          break;
        case "0003":
          produktbezeichnung.value = "Hausratversicherung";
          tibUrlEinProdukt.value = tibUrlMultiProduktVhv.value;
          populiereSb([0, 150, 250, 500]);
          break;
        default:
          tibUrlEinProdukt.value = "";
          break;
      }
      leistungen.value = leistungenFH(enthaeltVGV(piniaStore.state), enthaeltVHV(piniaStore.state));
    }

    function wechsleModule(): void {
      glasAnzeigen();
      hugAnzeigen();
      feuerrohbauAnzeigen();
      elementarAnzeigen();
      soforthilfeAnzeigen();
      photovoltaikAnzeigen();
      proklimaAnzeigen();
    }

    function feuerrohbauAnzeigen() {
      const currentDate = new Date();
      const baujahr = piniaStore.state.gebaeudedaten.baujahr;
      const minJahr = currentDate.getFullYear();
      const maxJahr = minJahr + 1;
      if (gte(baujahr, minJahr) && lte(baujahr, maxJahr)) {
        modulAnzeige("VGV2023_ROHBAUVERSICHERUNG", enthaeltVGV(piniaStore.state));
        if (piniaStore.state.isMakler) {
          feuerRohbauMakler();
        }
      } else {
        modulAnzeige("VGV2023_ROHBAUVERSICHERUNG", false);
        piniaStore.state.ferienhaus.vgv.tarifinformationen.rohbauversicherung = false;
      }
    }

    function feuerRohbauMakler() {
      const modul = produktmodule.value.find((i) => i.modulId === "rohbauversicherung");

      if (modul) {
        if (
          piniaStore.state.tarifinformationen.produktLinie === "002" ||
          piniaStore.state.tarifinformationen.produktLinie == "003"
        ) {
          modul.nameMakler = "Rohbauversicherung";
          modul.body =
            '<div class="o-grid__cell o-grid__cell--9">' +
            '<ul class="c-list">' +
            '<li class="c-list__item">Versicherte Gefahren während der Rohbauphase: Feuer, Sturm/Hagel, Leitungswasser</li>' +
            '<li class="c-list__item">Laufzeit bis zu 24 Monate</li>' +
            "</ul>" +
            "</div>";
        } else {
          modul.nameMakler = "Feuerrohbauversicherung";
          modul.body =
            '<div class="o-grid__cell o-grid__cell--9">' +
            '<ul class="c-list">' +
            '<li class="c-list__item">Versicherte Gefahren während der Rohbauphase: Feuer</li>' +
            '<li class="c-list__item">Laufzeit bis zu 24 Monate</li>' +
            "</ul>" +
            "</div>";
        }
      }
    }

    function glasAnzeigen() {
      modulAnzeige(
        "VHV2024_GLASBRUCH",
        istNurVHV(piniaStore.state) || (istVGVundVHV(piniaStore.state) && istEinfamilienhaus(piniaStore.state)),
      );
      modulAnzeige(
        "VGV2023_GLAS",
        istNurVGV(piniaStore.state) || (istVGVundVHV(piniaStore.state) && istMehrfamilienhaus(piniaStore.state)),
      );
    }

    function hugAnzeigen() {
      if (enthaeltVGV(piniaStore.state)) {
        modulAnzeige("HUG_EINFAMILIENHAUS", enthaeltVGV(piniaStore.state) && istEinfamilienhaus(piniaStore.state));
        modulAnzeige("HUG_MEHRFAMILIENHAUS", enthaeltVGV(piniaStore.state) && istMehrfamilienhaus(piniaStore.state));
      } else {
        modulAnzeige("HUG_EINFAMILIENHAUS", false);
        modulAnzeige("HUG_MEHRFAMILIENHAUS", false);
        piniaStore.state.tarifinformationen.hausundgrundbesitzerhaftpflicht = false;
      }
    }

    function elementarAnzeigen() {
      modulAnzeige("VHV2024_ELEMENTAR", false);
      modulAnzeige("VGV2023_ELEMENTARSCHUTZ", false);
      modulAnzeige("VGV2023_VHV_ELEMENTAR", false);

      if (istNurVHV(piniaStore.state)) {
        sollVHVElementarZeigen();
      }

      if (istNurVGV(piniaStore.state)) {
        sollVGVElementarZeigen();
      }

      if (istVGVundVHV(piniaStore.state)) {
        sollVGVundVHVElementarZeigen();
      }
    }

    function sollVHVElementarZeigen() {
      if (zeigeElementarDirekt() || piniaStore.state.isMakler) {
        modulAnzeige("VHV2024_ELEMENTAR", true);
      } else {
        modulAnzeige("VHV2024_ELEMENTAR", false);
        piniaStore.state.tarifinformationen.elementar = false;
      }
    }

    function sollVGVElementarZeigen() {
      if (!istBAKV() && (zeigeElementarDirekt() || piniaStore.state.isMakler)) {
        modulAnzeige("VGV2023_ELEMENTARSCHUTZ", true);
      } else {
        modulAnzeige("VGV2023_ELEMENTARSCHUTZ", false);
        piniaStore.state.tarifinformationen.elementar = false;
      }
    }

    function sollVGVundVHVElementarZeigen() {
      if (!istBAKV() && (zeigeElementarDirekt() || piniaStore.state.isMakler)) {
        modulAnzeige("VGV2023_VHV_ELEMENTAR", true);
      } else {
        modulAnzeige("VGV2023_VHV_ELEMENTAR", false);
        piniaStore.state.tarifinformationen.elementar = false;
      }
    }

    function sollAusschlussZeigen() {
      const modul = produktmodule.value.find((i) => i.modulId === "elementar");
      if (modul) {
        return !modul.anzeigen && istVGVundVHV(piniaStore.state) && istBAKV();
      }
      return false;
    }

    function zeigeElementarDirekt() {
      return !istZuersZone4();
    }

    function istZuersZone4() {
      return piniaStore.state.zuersZone.zone === "4";
    }

    function soforthilfeAnzeigen() {
      modulAnzeige("VGV2023_SOFORTHILFE", istNurVGV(piniaStore.state));
      modulAnzeige("VHV2024_SOFORTHILFE", !istNurVGV(piniaStore.state));
    }

    function photovoltaikAnzeigen() {
      if (
        enthaeltVGV(piniaStore.state) &&
        piniaStore.state.gebaeudedaten.photovoltaikVorhanden &&
        istPhotovoltaikVersicherungMoeglich(piniaStore.state)
      ) {
        modulAnzeige("VGV2023_ERNEUERBAREENERGIEN", true);
      } else {
        modulAnzeige("VGV2023_ERNEUERBAREENERGIEN", false);
        piniaStore.state.tarifinformationen.photovoltaikModul = false;
      }
    }

    function proklimaAnzeigen() {
      modulAnzeige("VHV2024_PROKLIMA", istNurVHV(piniaStore.state));
      modulAnzeige("VGV2023_PROKLIMA", istNurVGV(piniaStore.state));
      modulAnzeige("VGV2023_VHV_PROKLIMA", istVGVundVHV(piniaStore.state));
    }

    function modulAnzeige(preisId: string, schalter: boolean): void {
      const modul = produktmodule.value.find((i) => i.preisId === preisId);
      if (modul) modul.anzeigen = schalter;
    }

    function pruefeAufVorschaeden(): void {
      if (
        piniaStore.state.ferienhaus.vgv.tarifinformationen.vorschaedenAnzahl > 0 ||
        piniaStore.state.ferienhaus.vhv.tarifinformationen.vorschaedenAnzahl > 0
      ) {
        piniaStore.state.ferienhaus.vorschaedenVorhanden = 1;
      }
    }

    function istBAKV() {
      return piniaStore.state.gebaeudedaten.bauart === "0003" && piniaStore.state.gebaeudedaten.dachbelag === "1";
    }

    watch(
      () => [
        piniaStore.state.tarifinformationen.glasschutz,
        piniaStore.state.ferienhaus.versicherungsumfang,
        piniaStore.state.gebaeudedaten.gebaeudetyp,
      ],
      () => {
        if ((istVGVundVHV(piniaStore.state) && istEinfamilienhaus(piniaStore.state)) || istNurVHV(piniaStore.state)) {
          piniaStore.state.ferienhaus.vhv.tarifinformationen.glasschutz =
            piniaStore.state.tarifinformationen.glasschutz;
          piniaStore.state.ferienhaus.vgv.tarifinformationen.glasschutz = false;
        } else {
          piniaStore.state.ferienhaus.vhv.tarifinformationen.glasschutz = false;
          piniaStore.state.ferienhaus.vgv.tarifinformationen.glasschutz =
            piniaStore.state.tarifinformationen.glasschutz;
        }
      },
    );

    watch(
      () => [piniaStore.state.tarifinformationen.elementar, piniaStore.state.ferienhaus.versicherungsumfang],
      () => {
        if (istVGVundVHV(piniaStore.state)) {
          piniaStore.state.ferienhaus.vhv.tarifinformationen.elementar = piniaStore.state.tarifinformationen.elementar;
          piniaStore.state.ferienhaus.vgv.tarifinformationen.elementar = piniaStore.state.tarifinformationen.elementar;
        }
        if (istNurVGV(piniaStore.state)) {
          piniaStore.state.ferienhaus.vhv.tarifinformationen.elementar = false;
          piniaStore.state.ferienhaus.vgv.tarifinformationen.elementar = piniaStore.state.tarifinformationen.elementar;
        }
        if (istNurVHV(piniaStore.state)) {
          piniaStore.state.ferienhaus.vhv.tarifinformationen.elementar = piniaStore.state.tarifinformationen.elementar;
          piniaStore.state.ferienhaus.vgv.tarifinformationen.elementar = false;
        }
      },
    );

    watch(
      () => [piniaStore.state.tarifinformationen.soforthilfe, piniaStore.state.ferienhaus.versicherungsumfang],
      () => {
        if (enthaeltVGV(piniaStore.state)) {
          piniaStore.state.ferienhaus.vhv.tarifinformationen.soforthilfe = false;
          piniaStore.state.ferienhaus.vgv.tarifinformationen.soforthilfe =
            piniaStore.state.tarifinformationen.soforthilfe;
        } else {
          piniaStore.state.ferienhaus.vhv.tarifinformationen.soforthilfe =
            piniaStore.state.tarifinformationen.soforthilfe;
          piniaStore.state.ferienhaus.vgv.tarifinformationen.soforthilfe = false;
        }
      },
    );

    watch(
      () => [piniaStore.state.tarifinformationen.proklima, piniaStore.state.ferienhaus.versicherungsumfang],
      () => {
        if (enthaeltVGV(piniaStore.state)) {
          piniaStore.state.ferienhaus.vgv.tarifinformationen.proklima = piniaStore.state.tarifinformationen.proklima;
        }

        if (enthaeltVHV(piniaStore.state)) {
          piniaStore.state.ferienhaus.vhv.tarifinformationen.proklima = piniaStore.state.tarifinformationen.proklima;
        }
      },
    );

    watch(
      () => [piniaStore.state.tarifinformationen.photovoltaikModul, piniaStore.state.ferienhaus.versicherungsumfang],
      () => {
        if (enthaeltVGV(piniaStore.state)) {
          piniaStore.state.ferienhaus.vgv.tarifinformationen.photovoltaikModul =
            piniaStore.state.tarifinformationen.photovoltaikModul;
        } else {
          piniaStore.state.ferienhaus.vgv.tarifinformationen.photovoltaikModul = false;
        }
      },
    );

    watch(
      () => [
        piniaStore.state.ferienhaus.versicherungsumfang,
        piniaStore.state.zuersZone.erdbebenzone,
        piniaStore.state.zuersZone.zone,
      ],
      () => {
        wechsleTarif();
        wechsleModule();
      },
    );

    watch(
      () => piniaStore.state.ferienhaus.vorschaedenVorhanden,
      () => {
        if (piniaStore.state.ferienhaus.vorschaedenVorhanden === 1) {
          hatVorschaeden.value = true;
        } else {
          hatVorschaeden.value = false;
          piniaStore.state.ferienhaus.vgv.tarifinformationen.vorschaedenAnzahl = 0;
          piniaStore.state.ferienhaus.vhv.tarifinformationen.vorschaedenAnzahl = 0;
        }
      },
    );

    watch(
      () => piniaStore.state.tarifinformationen.selbstbeteiligung,
      () => {
        piniaStore.state.ferienhaus.vgv.tarifinformationen.selbstbeteiligung =
          piniaStore.state.tarifinformationen.selbstbeteiligung;
        piniaStore.state.ferienhaus.vhv.tarifinformationen.selbstbeteiligung =
          piniaStore.state.tarifinformationen.selbstbeteiligung;
      },
    );

    watch(
      () => piniaStore.state.tarifinformationen.rohbauversicherung,
      () => {
        if (enthaeltVGV(piniaStore.state)) {
          piniaStore.state.ferienhaus.vgv.tarifinformationen.rohbauversicherung =
            piniaStore.state.tarifinformationen.rohbauversicherung;
        } else {
          piniaStore.state.ferienhaus.vgv.tarifinformationen.rohbauversicherung = false;
        }
      },
    );
    watch(
      () => piniaStore.state.tarifinformationen.sfr,
      () => {
        piniaStore.state.ferienhaus.vgv.tarifinformationen.sfr = piniaStore.state.tarifinformationen.sfr;
        piniaStore.state.ferienhaus.vhv.tarifinformationen.sfr = piniaStore.state.tarifinformationen.sfr;
      },
    );

    watch(
      () => piniaStore.state.tarifinformationen.hugMitglied,
      () => {
        piniaStore.state.ferienhaus.vgv.tarifinformationen.hugMitglied =
          piniaStore.state.tarifinformationen.hugMitglied;
        piniaStore.state.ferienhaus.vhv.tarifinformationen.hugMitglied =
          piniaStore.state.tarifinformationen.hugMitglied;
        piniaStore.state.ferienhaus.hug.tarifinformationen.hugMitglied =
          piniaStore.state.tarifinformationen.hugMitglied;
      },
    );

    watch(
      () => piniaStore.state.tarifinformationen.zahlungsweise,
      () => {
        piniaStore.state.ferienhaus.vgv.tarifinformationen.zahlungsweise =
          piniaStore.state.tarifinformationen.zahlungsweise;
        piniaStore.state.ferienhaus.vhv.tarifinformationen.zahlungsweise =
          piniaStore.state.tarifinformationen.zahlungsweise;
        piniaStore.state.ferienhaus.hug.tarifinformationen.zahlungsweise =
          piniaStore.state.tarifinformationen.zahlungsweise;
      },
    );

    onMounted(() => {
      window.addEventListener("resize", behandleGroessenAenderung);
      wechsleTarif();
      wechsleModule();
      pruefeAufVorschaeden();
    });

    onUnmounted(() => {
      window.removeEventListener("resize", behandleGroessenAenderung);
    });

    onActivated(() => {
      wechsleTarif();
      wechsleModule();
    });

    function eingabenSindValide() {
      return new Promise((resolve) => {
        validiereForm("phForm").then(() => {
          Logger.debug("Anzahl Fehler aus Validierung: " + getFormFehlerAnzahl());
          fehler.value = getFormFehler();

          //-1 = Bitte wählen
          //TODO: Der Fehlerfall -1 existiert nicht, könnte also entfernt werden
          let fehlertext = "";
          if (piniaStore.state.tarifinformationen.selbstbeteiligung === -1) {
            fehlertext += "Bitte wählen Sie in den vorstehenden Tarifoptionen die Höhe der Selbstbeteiligung aus. ";
          }
          if (piniaStore.state.ferienhaus.vorschaedenVorhanden < 0) {
            fehlertext += "Bitte wählen Sie in den vorstehenden Tarifoptionen aus, ob Vorschäden vorhanden sind. ";
          }
          if (fehlertext) {
            fehler.value["tarifoptionen"] = fehlertext;
          }

          Logger.debug("Fehler-Objekt: ", fehler.value);
          resolve(getFormFehlerAnzahl() === 0);
        });
      });
    }

    function nextPage() {
      eingabenSindValide().then((isValid) => {
        if (isValid) {
          emit("next-page", { pageIndex: 2 });
        } else {
          document.querySelector("div#tariffOptionsTable")?.scrollIntoView({ behavior: "smooth" });
        }
      });
    }

    return {
      props,
      piniaStore,
      wechsleSichtbarkeit,
      istSichtbar,
      formatCurrency,
      fehler,
      produktbezeichnung,
      tarifoptionen,
      leistungen,
      tibUrlEinProdukt,
      tibUrlMultiProduktVgv,
      tibUrlMultiProduktVhv,
      produktmodule,
      nextPage,
      hatVorschaeden,
      enthaeltVGV,
      enthaeltVHV,
      ueberschwemmungText,
      sollAusschlussZeigen,
      ausschlussModul,
      istNurVHV,
    };
  },
});
</script>

<template>
  <steps v-bind:items="navItems" :active="2" @goto-page="$emit('goto-page', $event)" />

  <section>
    <tarifauswahl-eine-linie :produktbezeichnung="produktbezeichnung" />

    <leistungstabelle-eine-linie
      :inhalt="leistungen"
      :link="tibUrlEinProdukt"
      :link-fh-vgv="tibUrlMultiProduktVgv"
      :link-fh-vhv="tibUrlMultiProduktVhv"
    />

    <div id="tariffOptionsTable" class="o-grid">
      <div class="o-grid__cell o-grid__cell--12 o-grid__cell--3-480 u-text-align-right--480 u-form-grid-nospace-y">
        <h2 class="u-form-heading unset-text-align">
          {{ piniaStore.state.isMakler ? "Tarifmerkmale" : "Tarifoptionen" }}
        </h2>
      </div>
    </div>

    <tarifoption-eine-linie
      :tarifoptionInformation="tarifoptionen[0]"
      v-model.number="piniaStore.state.tarifinformationen.selbstbeteiligung"
    />
    <tarifoption-eine-linie
      :tarifoptionInformation="tarifoptionen[1]"
      v-model.number="piniaStore.state.tarifinformationen.sfr"
    />
    <tarifoption-eine-linie
      :tarifoptionInformation="tarifoptionen[2]"
      v-model.number="piniaStore.state.ferienhaus.vorschaedenVorhanden"
    />
    <tarifoption-eine-linie
      v-if="hatVorschaeden && enthaeltVGV(piniaStore.state)"
      :tarifoptionInformation="tarifoptionen[3]"
      v-model.number="piniaStore.state.ferienhaus.vgv.tarifinformationen.vorschaedenAnzahl"
    />
    <tarifoption-eine-linie
      v-if="hatVorschaeden && enthaeltVHV(piniaStore.state)"
      :tarifoptionInformation="tarifoptionen[4]"
      v-model.number="piniaStore.state.ferienhaus.vhv.tarifinformationen.vorschaedenAnzahl"
    />

    <div v-if="fehler.tarifoptionen" class="o-grid">
      <div
        class="o-grid__cell o-grid__cell--12 o-grid__cell--9-480 o-grid__cell--8-768 o-grid__cell--7-1024 o-grid__cell--6-1200 o-grid__cell--offset-3-480"
      >
        <div class="o-grid o-grid--no-y-gutter o-grid--align-flex-end">
          <div class="o-grid__cell o-grid__cell--12">
            <validierungs-fehler :fehler-text="fehler.tarifoptionen" />
          </div>
        </div>
      </div>
    </div>

    <produktmodul-ausschluss v-if="sollAusschlussZeigen()" :produktmodul-auschluss="ausschlussModul" />

    <produktmodul :produktmodule="produktmodule" :zeige-icons="true">
      <template v-slot:vhvElementarHinweis>
        <div class="o-grid__cell o-grid__cell--9">
          <ul class="c-list">
            <li class="c-list__item" :class="{ 'c-list--inactive': piniaStore.state.zuersZone.zone === '4' }">
              {{ ueberschwemmungText }}
            </li>
            <li class="c-list__item">Erdsenkung, Erdrutsch</li>
          </ul>
        </div>
      </template>
    </produktmodul>

    <div class="o-grid o-grid--align-middle u-mar-t-2">
      <div class="o-grid__cell o-grid__cell--12 o-grid__cell--3-480 u-text-align-right--480 u-form-grid-nospace-y">
        <h2 class="u-form-heading unset-text-align">Rabatte</h2>
      </div>
      <div
        class="o-grid__cell o-grid__cell--12 o-grid__cell--9-480 o-grid__cell--8-768 o-grid__cell--7-1024 o-grid__cell--6-1200 u-form-grid-nospace-y"
      >
        <hug-rabatt />
      </div>
    </div>
    <div v-if="istNurVHV(piniaStore.state)" class="o-grid o-grid--align-middle u-mar-t-1">
      <div
        class="o-grid__cell o-grid__cell--12 o-grid__cell--3-480 u-text-align-right--480 u-form-grid-nospace-y"
      ></div>
      <div
        class="o-grid__cell o-grid__cell--12 o-grid__cell--9-480 o-grid__cell--8-768 o-grid__cell--7-1024 o-grid__cell--6-1200 u-form-grid-nospace-y"
      >
        <div class="c-form-control-checkbox gev19__contexthelp">
          <input
            v-model="piniaStore.state.ferienhaus.vhv.tarifinformationen.buendelRabatt"
            id="buendelRabatt"
            name="buendelRabatt"
            class="c-form-control-checkbox__input"
            type="checkbox"
            tabindex="0"
            aria-label="Bündelrabatt"
            aria-describedby="tariffDiscountHuG"
            role="checkbox"
            :value="true"
            data-cy="buendelRabatt"
          />
          <label class="c-form-control-checkbox__label" for="buendelRabatt" style="max-width: 92%">
            <icon-sprite icon-name="icon-check-mark" css-klassen="c-form-control-checkbox__icon" :ist-klein="true" />
            <span class="c-form-control-checkbox__label-text">
              {{
                piniaStore.state.isMakler === true
                  ? "Der Versicherungsnehmer besitzt bereits eine GEV Wohngebäude-Versicherung für dieselbe Anschrift "
                  : "Ja, ich habe bereits eine GEV Wohngebäude-Versicherung für dieselbe Anschrift "
              }}<strong>(25% Rabatt)</strong>
            </span>
          </label>
        </div>
      </div>
    </div>

    <beitragFH @next-page="nextPage" />
    <email-oder-herunterladen produkt="fh" tracking-zusatz="tarif" />
  </section>
</template>

<style lang="scss">
.selbstbeteiligung {
  font-size: 1.2em;
  font-weight: 700;
}
</style>
